/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import authService, { User } from '@/services/authService';
import { setUserContext, clearUserContext } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, twoFactorToken?: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar si hay un usuario almacenado al cargar
    const checkAuth = async () => {
      try {
        console.log('Checking auth...');
        const storedUser = authService.getStoredUser();
        console.log('Stored user:', storedUser);
        
        if (storedUser && authService.isAuthenticated()) {
          console.log('User authenticated, verifying token...');
          // Verificar que el token sigue siendo válido
          const currentUser = await authService.getCurrentUser();
          console.log('Current user from API:', currentUser);
          setUser(currentUser);

          // Configurar usuario en Sentry para tracking de errores
          setUserContext({
            id: currentUser._id,
            email: currentUser.email,
            username: currentUser.firstName ? `${currentUser.firstName} ${currentUser.lastName || ''}`.trim() : undefined,
          });
        } else {
          console.log('No user stored or not authenticated');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Si el token no es válido, limpiar localStorage
        authService.logout();
      } finally {
        setLoading(false);
        console.log('Auth check complete');
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string, twoFactorToken?: string) => {
    const loginData = { email, password, twoFactorToken };
    const response = await authService.login(loginData);
    setUser(response.data);

    // Configurar usuario en Sentry después del login
    setUserContext({
      id: response.data._id,
      email: response.data.email,
      username: response.data.firstName ? `${response.data.firstName} ${response.data.lastName || ''}`.trim() : undefined,
    });
  };

  const register = async (name: string, email: string, password: string) => {
    const response = await authService.register({ name, email, password });
    setUser(response.data);

    // Configurar usuario en Sentry después del registro
    setUserContext({
      id: response.data._id,
      email: response.data.email,
      username: response.data.firstName ? `${response.data.firstName} ${response.data.lastName || ''}`.trim() : undefined,
    });
  };

  const logout = () => {
    authService.logout();
    setUser(null);

    // Limpiar usuario de Sentry al hacer logout
    clearUserContext();
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  console.log('AuthContext value:', value);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 